import React, { useMemo, useCallback } from 'react';
import { ThemeProvider } from '@mui/material';
import { themeCreator } from './base';
import { StylesProvider } from '@mui/styles';
import { useSettingsContext } from 'src/hooks/useSettings';

type ThemeContextValue = {
  themeName: string;
  setThemeName: (themeName: string) => void;
};

const DEFAULT_THEME = 'DarkTheme';

export const ThemeContext = React.createContext<ThemeContextValue>({
  themeName: DEFAULT_THEME,
  setThemeName: () => {}
});

const ThemeProviderWrapper: React.FC = (props) => {
  const { settings, setSetting } = useSettingsContext();

  // Use settings.theme if set; otherwise fall back to old localStorage; otherwise default.
  // IMPORTANT: this does NOT write anything to settings until the user changes the theme.
  const themeName =
    settings?.theme ||
    localStorage.getItem('appTheme') ||
    DEFAULT_THEME;

  const theme = useMemo(() => themeCreator(themeName), [themeName]);

  const setThemeName = useCallback(
    (nextThemeName: string) => {
      setSetting('theme', nextThemeName);
      // Optional: keep this ONLY if you want backwards compatibility during rollout
      // localStorage.setItem('appTheme', nextThemeName);
    },
    [setSetting]
  );

  const ctxValue = useMemo(
    () => ({ themeName, setThemeName }),
    [themeName, setThemeName]
  );

  return (
    <StylesProvider injectFirst>
      <ThemeContext.Provider value={ctxValue}>
        <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

export default ThemeProviderWrapper;